import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';

gsap.registerPlugin(Draggable, InertiaPlugin);

export default class MobileCategorySwipe {
  constructor({ element }) {
    if (!element)
      throw new Error('MobileCategorySwipe requires a DOM element.');

    this.el = element;
    this.wrapper = this.el.querySelector('.menu__category__wrapper');
    if (!this.wrapper)
      throw new Error(
        'MobileCategorySwipe requires a wrapper inside .menu__category',
      );

    this.items = Array.from(this.wrapper.children);
    this.activeIndex = 0;
    this.mm = gsap.matchMedia();

    this.mm.add('(max-width: 768px)', () => {
      this._initDrag();
      this._initClickSnap();
      return () => this._destroyDrag();
    });
  }

  _getBounds() {
    const containerWidth = this.wrapper.parentNode.offsetWidth;
    const totalWidth = this.wrapper.scrollWidth;
    return {
      minX: Math.min(containerWidth - totalWidth, 0),
      maxX: 0,
    };
  }

  _snapToItem(index, animate = true) {
    const item = this.items[index];
    if (!item) return;

    // Kill any in-flight tween before starting a new one
    gsap.killTweensOf(this.wrapper);

    const containerWidth = this.wrapper.parentNode.offsetWidth;
    const { minX, maxX } = this._getBounds();

    const itemCentre = item.offsetLeft + item.offsetWidth / 2;
    let targetX = containerWidth / 2 - itemCentre;
    targetX = Math.min(maxX, Math.max(minX, targetX));

    this.activeIndex = index;
    this._setActiveClass(item);

    if (animate) {
      gsap.to(this.wrapper, {
        x: targetX,
        duration: 0.4,
        ease: 'power2.out', // softer ease — less overshoot feel
      });
    } else {
      gsap.set(this.wrapper, { x: targetX });
    }
  }

  _setActiveClass(activeItem) {
    this.items.forEach(item => item.classList.remove('is-active'));
    activeItem.classList.add('is-active');
  }

  _snapToNearest() {
    // Find whichever item centre is closest to the container centre
    const containerWidth = this.wrapper.parentNode.offsetWidth;
    const currentX = gsap.getProperty(this.wrapper, 'x');
    const containerCentre = containerWidth / 2;

    let closestIndex = 0;
    let closestDist = Infinity;

    this.items.forEach((item, i) => {
      const itemCentre = item.offsetLeft + item.offsetWidth / 2 + currentX;
      const dist = Math.abs(itemCentre - containerCentre);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    });

    this._snapToItem(closestIndex, true);
  }

  _initDrag() {
    if (this.draggable) return;

    const { minX, maxX } = this._getBounds();
    gsap.set(this.wrapper, { x: 0 });

    this.draggable = Draggable.create(this.wrapper, {
      type: 'x',
      edgeResistance: 0.65, // slight give at edges without fighting inertia
      inertia: true,
      bounds: { minX, maxX },
      zIndexBoost: false,
      throwProps: true,
      // Remove throwResistance entirely — let InertiaPlugin handle deceleration naturally
      onThrowComplete: () => this._snapToNearest(),
      onDragEnd: function () {
        // Only snap if throw didn't take over (very slow drags)
        if (!this.isThrowing) this._snapToNearest();
      }.bind(this),
    });

    this._resizeObserver = new ResizeObserver(() => {
      // Debounce resize snapping to avoid mid-resize jitter
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        const { minX: newMin, maxX: newMax } = this._getBounds();
        this.draggable[0].applyBounds({ minX: newMin, maxX: newMax });
        this._snapToItem(this.activeIndex, false);
      }, 100);
    });
    this._resizeObserver.observe(this.wrapper.parentNode);
  }

  _initClickSnap() {
    this.items.forEach((item, index) => {
      item.addEventListener('click', () => {
        if (index === this.activeIndex) return;
        this._snapToItem(index, true);
      });
    });
  }

  _destroyDrag() {
    if (this.draggable) {
      this.draggable.forEach(d => d.kill());
      this.draggable = null;
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    clearTimeout(this._resizeTimer);
    gsap.killTweensOf(this.wrapper);
    gsap.set(this.wrapper, { clearProps: 'x' });
    this.items.forEach(item => item.classList.remove('is-active'));
  }
}
